/**
 * Speed test engine — multi-connection, Ookla-style.
 *
 * Uses Cloudflare public endpoints (CORS-enabled, no key) as primary, our
 * backend as fallback:
 *   - https://speed.cloudflare.com/__down?bytes=N
 *   - https://speed.cloudflare.com/__up
 *
 * Runs PARALLEL_STREAMS concurrent fetches to saturate the link (single TCP
 * streams underreport on high-latency / high-bandwidth paths because of the
 * TCP receive window).
 */

import { API } from "./api";

const CF_DOWN = "https://speed.cloudflare.com/__down?bytes=";
const CF_UP = "https://speed.cloudflare.com/__up";
const CF_PING = "https://speed.cloudflare.com/__down?bytes=1000";

const PARALLEL_STREAMS = 6;
const TEST_DURATION_MS = 10_000;

// ---------- PING / JITTER ----------
async function measurePing(samples = 10, onProgress) {
  const latencies = [];
  for (let i = 0; i < samples; i++) {
    const t0 = performance.now();
    try {
      await fetch(`${CF_PING}&cb=${Math.random()}`, { cache: "no-store" });
      latencies.push(performance.now() - t0);
    } catch {}
    if (onProgress) onProgress((i + 1) / samples);
    await sleep(50);
  }
  if (!latencies.length) return { pingMs: 0, jitterMs: 0 };
  latencies.sort((a, b) => a - b);
  const trimmed = latencies.length > 4 ? latencies.slice(1, -1) : latencies;
  const avg = trimmed.reduce((s, v) => s + v, 0) / trimmed.length;
  const jitter =
    trimmed.length > 1
      ? trimmed.slice(1).reduce((s, v, i) => s + Math.abs(v - trimmed[i]), 0) /
        (trimmed.length - 1)
      : 0;
  return { pingMs: Math.round(avg), jitterMs: Math.round(jitter) };
}

// ---------- Shared measurement loop ----------
function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

async function runParallel({ label, durationMs, onProgress, worker }) {
  const t0 = performance.now();
  const state = {
    totalBytes: 0,
    stop: false,
    peakMbps: 0,
  };

  const tick = setInterval(() => {
    if (state.stop) return;
    const elapsed = (performance.now() - t0) / 1000;
    if (elapsed <= 0) return;
    const mbps = (state.totalBytes * 8) / (elapsed * 1_000_000);
    state.peakMbps = Math.max(state.peakMbps, mbps);
    if (onProgress) onProgress({ mbps, progress: Math.min(elapsed / (durationMs / 1000), 1) });
  }, 200);

  const workers = [];
  for (let i = 0; i < PARALLEL_STREAMS; i++) {
    workers.push(worker(state, t0, durationMs));
  }

  // Stop at deadline
  setTimeout(() => { state.stop = true; }, durationMs);

  await Promise.all(workers);
  clearInterval(tick);

  const elapsed = (performance.now() - t0) / 1000;
  const avgMbps = elapsed > 0 ? (state.totalBytes * 8) / (elapsed * 1_000_000) : 0;

  // Report something between the 85% mark and peak (Ookla-style).
  // Use max(avg, peak*0.9) to reflect sustained throughput near the end.
  const finalMbps = Math.max(avgMbps, state.peakMbps * 0.9);
  return { mbps: Number(finalMbps.toFixed(2)), totalBytes: state.totalBytes };
}

// ---------- DOWNLOAD ----------
async function measureDownload({ onProgress, durationMs = TEST_DURATION_MS }) {
  const CHUNK_SIZES = [10_000_000, 25_000_000, 50_000_000]; // scale up for faster links

  async function worker(state) {
    let chunkIdx = 0;
    while (!state.stop) {
      const size = CHUNK_SIZES[Math.min(chunkIdx, CHUNK_SIZES.length - 1)];
      let url = `${CF_DOWN}${size}&cb=${Math.random()}`;
      try {
        const resp = await fetch(url, { cache: "no-store" });
        if (!resp.ok || !resp.body) {
          // backend fallback
          const r2 = await fetch(`${API}/speedtest/download?bytes=${size}&cb=${Math.random()}`, { cache: "no-store" });
          if (!r2.ok || !r2.body) { state.stop = true; return; }
          await consumeStream(r2.body, (b) => { state.totalBytes += b; }, state);
        } else {
          await consumeStream(resp.body, (b) => { state.totalBytes += b; }, state);
        }
      } catch {
        return;
      }
      chunkIdx++;
    }
  }

  const { mbps } = await runParallel({
    label: "download",
    durationMs,
    onProgress,
    worker,
  });
  return { downloadMbps: mbps };
}

async function consumeStream(stream, onChunk, state) {
  const reader = stream.getReader();
  try {
    while (true) {
      if (state && state.stop) { try { await reader.cancel(); } catch {}; return; }
      const { done, value } = await reader.read();
      if (done) return;
      if (value) onChunk(value.byteLength);
    }
  } catch {
    return;
  }
}

// ---------- UPLOAD ----------
async function measureUpload({ onProgress, durationMs = TEST_DURATION_MS }) {
  // Pre-generate a 4MB payload once and reuse
  const CHUNK_SIZE = 4_000_000; // 4 MB per request
  const payload = new Uint8Array(CHUNK_SIZE);
  crypto.getRandomValues(payload.subarray(0, 4096));
  for (let i = 4096; i < CHUNK_SIZE; i += 4096) payload.copyWithin(i, 0, 4096);

  async function worker(state) {
    while (!state.stop) {
      let ok = false;
      try {
        const r = await fetch(CF_UP, {
          method: "POST",
          body: payload,
          cache: "no-store",
          headers: { "Content-Type": "application/octet-stream" },
        });
        ok = r.ok;
      } catch { ok = false; }

      if (!ok) {
        try {
          const r2 = await fetch(`${API}/speedtest/upload`, {
            method: "POST",
            body: payload,
            cache: "no-store",
            headers: { "Content-Type": "application/octet-stream" },
          });
          ok = r2.ok;
        } catch { ok = false; }
      }
      if (!ok) return;
      state.totalBytes += CHUNK_SIZE;
    }
  }

  const { mbps } = await runParallel({
    label: "upload",
    durationMs,
    onProgress,
    worker,
  });
  return { uploadMbps: mbps };
}

// ---------- Traffic Light Verdict ----------
export function computeVerdict({ pingMs, downloadMbps, uploadMbps }) {
  if (pingMs > 150 || downloadMbps < 5 || uploadMbps < 1) {
    return {
      level: "poor",
      label: "Needs Attention",
      message:
        "Expect buffering on video and laggy calls. Try restarting your router or moving closer to it.",
    };
  }
  if (pingMs > 60 || downloadMbps < 25 || uploadMbps < 3) {
    return {
      level: "fair",
      label: "Usable",
      message:
        "Fine for HD streaming and regular browsing, but 4K video and large uploads may struggle.",
    };
  }
  return {
    level: "good",
    label: "Excellent",
    message:
      "Great for 4K streaming, smooth video calls and online gaming. You're good to go.",
  };
}

// ---------- Activity Suitability ----------
// Each activity: { id, name, icon-key, requirement -> level+hint }
export function computeActivities({ pingMs, jitterMs, downloadMbps, uploadMbps }) {
  const check = (ok, warn) => (ok ? "good" : warn ? "fair" : "poor");

  const list = [
    {
      id: "browsing",
      name: "Web browsing",
      icon: "globe",
      level: downloadMbps >= 10 ? "good" : downloadMbps >= 3 ? "fair" : "poor",
      hint: downloadMbps >= 10 ? "Snappy" : downloadMbps >= 3 ? "A bit slow" : "Painful",
    },
    {
      id: "hd",
      name: "HD streaming",
      icon: "tv",
      level: downloadMbps >= 10 ? "good" : downloadMbps >= 5 ? "fair" : "poor",
      hint: downloadMbps >= 10 ? "1080p, no buffering" : downloadMbps >= 5 ? "Occasional buffer" : "Drops to SD",
    },
    {
      id: "4k",
      name: "4K streaming",
      icon: "film",
      level: downloadMbps >= 25 && pingMs < 150 ? "good" : downloadMbps >= 15 ? "fair" : "poor",
      hint: downloadMbps >= 25 ? "Silky 4K" : downloadMbps >= 15 ? "May dip to 1080p" : "Not feasible",
    },
    {
      id: "video-calls",
      name: "Video calls",
      icon: "video",
      level:
        downloadMbps >= 3 && uploadMbps >= 1.5 && pingMs < 100 && jitterMs < 30
          ? "good"
          : downloadMbps >= 1.5 && uploadMbps >= 0.8 && pingMs < 200
          ? "fair"
          : "poor",
      hint:
        uploadMbps >= 1.5 && pingMs < 100 ? "Crisp HD calls" : uploadMbps >= 0.8 ? "Occasional freeze" : "Laggy / choppy",
    },
    {
      id: "gaming",
      name: "Online gaming",
      icon: "gamepad",
      level:
        pingMs < 50 && jitterMs < 20 && downloadMbps >= 5
          ? "good"
          : pingMs < 100 && jitterMs < 40
          ? "fair"
          : "poor",
      hint:
        pingMs < 50 ? "Competitive-ready" : pingMs < 100 ? "Casual OK" : "Rubber-banding likely",
    },
    {
      id: "uploads",
      name: "Uploads / cloud sync",
      icon: "upload-cloud",
      level: uploadMbps >= 10 ? "good" : uploadMbps >= 3 ? "fair" : "poor",
      hint: uploadMbps >= 10 ? "Big files fly" : uploadMbps >= 3 ? "Photos fine, video slow" : "Will crawl",
    },
  ];

  // Suppress unused helper warning
  void check;
  return list;
}

// ---------- Public API ----------
export async function runSpeedTest({ onStage, onProgress } = {}) {
  if (onStage) onStage("ping");
  const { pingMs, jitterMs } = await measurePing(10, (p) =>
    onProgress && onProgress({ stage: "ping", progress: p })
  );

  if (onStage) onStage("download");
  const { downloadMbps } = await measureDownload({
    onProgress: (p) => onProgress && onProgress({ stage: "download", ...p }),
  });

  if (onStage) onStage("upload");
  const { uploadMbps } = await measureUpload({
    onProgress: (p) => onProgress && onProgress({ stage: "upload", ...p }),
  });

  if (onStage) onStage("done");
  return { pingMs, jitterMs, downloadMbps, uploadMbps };
}
